import * as THREE from 'three';
import type { BattleState, Enemy, Hero, Zone } from '@somnhold/sim';
import type { UniqueId } from '@somnhold/content';
import type { GlowLayer } from './glow.ts';
import type { Vfx } from './vfx.ts';

/*
 * The unique pieces' forms, seen (docs/UNICAS.md §6, docs/PODERES.md §5): each form a look of its own in its type's
 * palette, the heroes' brighter and cleaner than the nightmares'. Nothing here owns a mesh or a material: everything
 * is the shared pooled primitives (the vfx's sparks, rings, beams and shafts, the entity glow and the flares), so a
 * form costs instances, never a draw call, a program or a light. What lasts (the forms' own ground and the marks on a
 * skill's zone) is placed every frame from the state; what bursts (`boom`, `beam`) comes from the tick's events.
 * Particles are only spawned on a fresh tick (`fresh`), never per frame.
 */

export interface FormFxCtx {
  vfx: Vfx;
  glow: GlowLayer;
  flare: (p: THREE.Vector3, color: string, size: number, life: number) => void;
  shake: (n: number) => void;
  /** Something to run `n` ticks from now (the world's queue). */
  later: (n: number, run: () => void) => void;
  low: () => boolean;
  /** A sim point on the ground, `h` above it. */
  at: (x: number, y: number, h?: number) => THREE.Vector3;
  /** The same, written into `out` (for what runs every frame: no new vectors). */
  atTo: (x: number, y: number, h: number, out: THREE.Vector3) => THREE.Vector3;
  /** Fixed sim units to world units. */
  k: number;
}

const C = (hex: string): THREE.Color => new THREE.Color(hex);
// Palettes by type (PODERES §5). No gold: holy is a cold white.
const STONE = C('#dfe7ef');
const STEEL = C('#c4c8d2');
const EMBER = C('#ff6a2a');
const EMBER_LT = C('#ffb347');
const FROSTC = C('#9fdcff');
const BOLT = C('#8fb4ff');
const BOLT_LT = C('#dff0ff');
const HOLY = C('#eef2ff');
const HOLY_BLUE = C('#cfe0ff');
const LEAF = C('#7fc06a');
const LEAF_LT = C('#b3f59a');
const ROOT = C('#4f7a3a');
const REMEDY = C('#7fe0c0');
const DRAGON = C('#ff8a6e');
const POLLEN = C('#e4f5d8');
const PURE = C('#ffffff');
/** Form flashes a tick at most, and their biggest size (see `fl`). */
const FLARES = 3;
const FLARE_MAX = 1.5;
const SCAR = C('#2a2420');
const TYPE_COLOR: Record<string, [THREE.Color, string, string]> = {
  fire: [EMBER, '#ff5a1f', '#ffb347'],
  frost: [FROSTC, '#9fdcff', '#e8f7ff'],
  lightning: [BOLT, '#8fb4ff', '#ffffff'],
  poison: [LEAF, '#6fa05a', '#b3f59a'],
  acid: [C('#b5e06a'), '#8fbf3a', '#d8f59a'],
  shadow: [C('#9a86d0'), '#6a5aa0', '#d6ccff'],
  holy: [HOLY, '#dfe8ff', '#ffffff'],
  physical: [STEEL, '#8a8172', '#dfe7ef'],
};

const TAU = Math.PI * 2;

/** `?formfx=0`: the forms drawn as their skills alone, no look of their own (to compare a fight with and without it). */
export const FORM_FX = { on: true };

export class FormFx {
  private v = new THREE.Vector3();
  private w = new THREE.Vector3();
  /** Zones already seen being born (their birth is drawn once). */
  private seen = new Set<number>();
  private seenAt = 0;

  constructor(private c: FormFxCtx) {}

  private n(full: number, low: number): number {
    return this.c.low() ? low : full;
  }

  /** Glow dots along a ground segment (a crack, a line of fire): every `step` world units, `size` each. */
  private line(a: THREE.Vector3, b: THREE.Vector3, color: THREE.Color, size: number, opacity: number, step = 0.45): void {
    const len = a.distanceTo(b);
    const n = Math.max(2, Math.min(24, Math.ceil(len / step)));
    for (let i = 0; i <= n; i++) {
      this.v.copy(a).lerp(b, i / n);
      this.c.glow.add(this.v.x, this.v.y, this.v.z, color, size, opacity);
    }
  }

  /** Glow dots around a ring on the ground, turning slowly (`spin`: turns a second). */
  private rim(p: THREE.Vector3, r: number, color: THREE.Color, n: number, size: number, opacity: number, t: number, spin = 0.1): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + t * spin * TAU;
      this.c.glow.add(p.x + Math.cos(a) * r, p.y + 0.06, p.z + Math.sin(a) * r, color, size, opacity);
    }
  }

  /** A point at random within a disc (for sparks). */
  private inDisc(p: THREE.Vector3, r: number, h = 0.05): THREE.Vector3 {
    const a = Math.random() * TAU;
    const d = Math.sqrt(Math.random()) * r;
    return this.w.set(p.x + Math.cos(a) * d, p.y + h, p.z + Math.sin(a) * d).clone();
  }

  /** Whether this zone is seen for the first time (its birth), keeping the memory short. */
  private born(s: BattleState, id: number): boolean {
    if (s.tick - this.seenAt > 120) {
      const alive = new Set(s.zones.map((z) => z.id));
      for (const k of this.seen) if (!alive.has(k)) this.seen.delete(k);
      this.seenAt = s.tick;
    }
    if (this.seen.has(id)) return false;
    this.seen.add(id);
    return true;
  }

  /**
   * A form's flash, kept in check (2026-11-02, the art review: several forms at once burnt a white cloud over the heroes
   * and their names on a phone): at most FLARES a tick, never bigger than FLARE_MAX, and a white one turned to the cold
   * blue of light, so what stays white is the hero's own outline and the rings, not a fill.
   */
  private flaresLeft = FLARES;
  private readonly fl = (p: THREE.Vector3, color: string, size: number, life: number): void => {
    if (this.flaresLeft <= 0) return;
    this.flaresLeft--;
    const white = color === '#ffffff' || color === '#eef2ff';
    this.c.flare(p, white ? '#bcd2ff' : color, Math.min(FLARE_MAX, size * (white ? 0.7 : 0.8)), life);
  };

  /** The form bits a hero carries a piece in (the sim's `uqForm`, parallel to `uq`). */
  private bits(h: Hero, id: UniqueId): number {
    const i = h.uq?.indexOf(id) ?? -1;
    return i >= 0 ? (h.uqForm?.[i] ?? 0) : 0;
  }

  /** Nightmares under the heavy hammer, kept from the last fresh tick (drawn every frame). */
  private heavy: Enemy[] = [];
  /** Scratch points for what is drawn every frame (no new vectors per frame). */
  private pa = new THREE.Vector3();
  private pb = new THREE.Vector3();
  private pq = new THREE.Vector3();

  /* ———————————————— what lasts on heroes, summons and nightmares ———————————————— */

  /**
   * The forms seen on what carries them, once a frame (world drawZones): the slowing beam's frost along the turrets'
   * beam, the iron guard's plates round the artificer, the moss on the bear, the second dawn's sun waiting over the
   * ally, the heavy hammer's crater of light, the bitter sap dripping from the held. Only heroes that carry a form are
   * looked at; nightmares only on a fresh tick, and only when someone carries the form that marks them.
   */
  units(s: BattleState, fresh: boolean, t: number): void {
    if (!FORM_FX.on) return;
    const { vfx, glow } = this.c;
    if (fresh) this.flaresLeft = FLARES;
    let sap = false;
    let hammer = false;
    let dawn = false;
    for (const h of s.heroes) {
      if (!h.uqForm) continue;
      if (this.bits(h, 'oldRootStaff') & 1) sap = true;
      if (this.bits(h, 'gasparHammer') & 2) hammer = true;
      if (this.bits(h, 'healerRosary') & 4) dawn = true;
      // Haz que frena: frost running along the beams between the artificer's turrets.
      if (this.bits(h, 'garrisonKey') & 1) {
        let k = 0;
        for (let i = 0; i < s.turrets.length; i++) {
          const a = s.turrets[i]!;
          if (a.owner !== h.id || a.portable) continue;
          for (let j = i + 1; j < s.turrets.length; j++) {
            const b = s.turrets[j]!;
            if (k >= 3 || b.owner !== h.id || b.portable || (a.x - b.x) ** 2 + (a.y - b.y) ** 2 > 3500 * 3500) continue;
            const pa = this.c.atTo(a.x, a.y, 0.9, this.pa);
            const pb = this.c.atTo(b.x, b.y, 0.9, this.pb);
            // A frost sheath along the whole beam, and beads of rime running down it.
            // Frost-blue, thin and sparse (a few beams cross over the artificer: they must not burn white there).
            this.line(pa, pb, FROSTC, 0.26, 0.14, 0.8);
            for (let m = 0; m < 6; m++) {
              const f = (t * 0.6 + m / 6 + k * 0.13) % 1;
              this.v.copy(pa).lerp(pb, f);
              glow.add(this.v.x, this.v.y, this.v.z, FROSTC, 0.34, 0.5);
            }
            // Its ends frosted over: a crown of rime on each turret.
            this.rim(this.pq.copy(pa).setY(pa.y - 0.5), 0.4, FROSTC, 5, 0.16, 0.45, t, 0.2);
            if (fresh && (s.tick + k) % 4 === 0) vfx.burst(this.v.copy(pa).lerp(pb, Math.random()), { n: 2, color: '#e8f7ff', speed: 0.02, up: -0.01, size: 0.07, life: 12, gravity: 0.004 });
            k++;
          }
        }
      }
      if (h.downTicks > 0) continue;
      // Escudo de hierro: iron plates turning round the artificer while its automaton stands close, a thread to it.
      if (this.bits(h, 'anvilHarness') & 1 && s.bots) {
        for (const b of s.bots) {
          if (b.owner !== h.id || !b.follow || b.hp <= 0 || (b.x - h.x) ** 2 + (b.y - h.y) ** 2 > 2000 * 2000) continue;
          const p = this.c.atTo(h.x, h.y, 0.9, this.pa);
          for (let m = 0; m < 6; m++) {
            const a = (m / 6) * TAU + t * 0.5;
            glow.add(p.x + Math.cos(a) * 0.75, p.y + 0.1 * Math.sin(t * 3 + m), p.z + Math.sin(a) * 0.75, m % 3 ? STEEL : EMBER_LT, 0.42, 0.6);
          }
          this.line(p, this.c.atTo(b.x, b.y, 1, this.pb), STEEL, 0.14, 0.4, 0.5);
          if (fresh && s.tick % 18 === 0) vfx.ring(this.pq.copy(p).setY(p.y - 0.8), 0.4, 0.95, '#c4c8d2', 6, 0.45);
          break;
        }
      }
      // Musgo: green moss creeping over the bear, a breath of leaves every second.
      if ((h.bear ?? 0) > 0 && this.bits(h, 'dwarfBark') & 1) {
        const p = this.c.atTo(h.x, h.y, 1.1, this.pa);
        for (let m = 0; m < 9; m++) {
          const a = m * 2.4 + t * 0.15;
          const r = 0.25 + 0.35 * ((m * 37) % 10) / 10;
          glow.add(p.x + Math.cos(a) * r, p.y + 0.15 * Math.sin(a * 2), p.z + Math.sin(a) * r, m % 3 ? LEAF : LEAF_LT, 0.3, 0.5);
        }
        if (fresh && s.tick % 12 === 0) {
          vfx.burst(p, { n: this.n(5, 2), color: '#7fc06a', speed: 0.03, up: 0.03, size: 0.1, life: 14, gravity: -0.002, spread: 0.6 });
          vfx.ring(this.pq.copy(p).setY(p.y - 1), 0.3, 0.9, '#b3f59a', 6, 0.4);
        }
      }
    }
    // Segundo amanecer: a little sun waiting over every ally the dawn watches, its rays turning.
    if (dawn) {
      for (const o of s.heroes) {
        if (o.downTicks > 0 || !((o.intercede ?? 0) > 0)) continue;
        // High over the name, a small core in a ring of rays: the ally and its name stay readable under it.
        const p = this.c.atTo(o.x, o.y, 3.2 + 0.08 * Math.sin(t * 2), this.pa);
        const k = Math.min(1, (o.intercede ?? 0) / 24);
        glow.add(p.x, p.y, p.z, HOLY_BLUE, 0.6, 0.45 * k + 0.2);
        glow.add(p.x, p.y, p.z, DRAGON, 1.0, 0.12);
        this.rim(p, 0.55, HOLY_BLUE, 10, 0.2, 0.5 * k + 0.2, t, 0.25);
        this.rim(p, 0.8, HOLY, 5, 0.14, 0.35 * k + 0.1, t, -0.15);
        // A thin thread of light down to the ally it waits over.
        for (let m = 1; m < 3; m++) glow.add(p.x, p.y - m * 0.3, p.z, HOLY_BLUE, 0.1, 0.3);
      }
    }
    // The nightmares: looked at on a fresh tick only, kept for the frames between.
    if (fresh && hammer) {
      this.heavy.length = 0;
      for (const e of s.enemies) if ((e.holyT ?? 0) > s.tick && e.hp > 0) this.heavy.push(e);
    } else if (!hammer) this.heavy.length = 0;
    // Martillo pesado: a crater of cold light under the smitten, the hammer's mark hanging over it.
    for (const e of this.heavy) {
      if (e.hp <= 0) continue;
      const p = this.c.atTo(e.x, e.y, 0.06, this.pa);
      // The crater's rim, its cold core, and the hammer's mark hanging over it (head and haft), edged in dragon red.
      this.rim(p, 0.8, HOLY, 12, 0.3, 0.65, t, 0.05);
      this.rim(p, 0.95, DRAGON, 12, 0.16, 0.45, t, -0.05);
      glow.add(p.x, p.y + 0.05, p.z, HOLY_BLUE, 1.6, 0.16);
      glow.add(p.x - 0.22, p.y + 2.3, p.z, HOLY, 0.42, 0.75);
      glow.add(p.x + 0.22, p.y + 2.3, p.z, HOLY, 0.42, 0.75);
      for (let m = 0; m < 4; m++) glow.add(p.x, p.y + 2.15 - m * 0.18, p.z, m ? HOLY_BLUE : HOLY, 0.22, 0.6);
    }
    // Savia amarga: bitter sap dripping from what the roots hold and poison.
    if (sap && fresh && s.tick % 3 === 0) {
      let k = 0;
      for (const e of s.enemies) {
        if (e.hp <= 0 || e.dotType !== 'poison' || e.dotTicks <= 0 || e.slowBp < 5000 || e.slowTicks <= 0) continue;
        const p = this.c.atTo(e.x, e.y, 1.1, this.pa);
        vfx.burst(p, { n: 1, color: k % 2 ? '#b3f59a' : '#6fa05a', speed: 0.01, up: 0.01, size: 0.09, life: 14, gravity: 0.012, spread: 0.4 });
        if (s.tick % 9 === 0) vfx.ring(this.c.atTo(e.x, e.y, 0.05, this.pb), 0.1, 0.4, '#6fa05a', 6, 0.5);
        if (++k >= this.n(24, 10)) break;
      }
    }
  }

  /* ———————————————— the forms' own ground ———————————————— */

  /** Draws one of the forms' own zones; false when the kind isn't one of them. */
  zone(s: BattleState, z: Zone, p: THREE.Vector3, fresh: boolean, t: number): boolean {
    if (!FORM_FX.on) return true;
    const { vfx } = this.c;
    const rr = z.radius * this.c.k;
    switch (z.kind) {
      case 'cleft': {
        // Rajadura: the ground split open ahead of the blow, a pale-lit seam with rubble jumping out of it.
        const b = this.c.at(z.tx ?? z.x, z.ty ?? z.y, 0.05);
        const fade = Math.min(1, z.ticks / 8);
        this.line(p, b, SCAR, rr * 2.2, 0.55 * fade, 0.35);
        this.line(p, b, STONE, rr * 0.9, 0.5 * fade, 0.3);
        this.line(p, b, DRAGON, rr * 0.45, 0.35 * fade, 0.5);
        if (fresh && this.born(s, z.id)) {
          const n = this.n(8, 4);
          for (let i = 0; i <= n; i++) {
            const q = p.clone().lerp(b, i / n);
            this.c.later(Math.floor(i / 2), () => {
              vfx.burst(q.clone().setY(q.y + 0.1), { n: this.n(5, 2), color: '#8a8172', speed: 0.07, up: 0.09, size: 0.12, life: 10, gravity: 0.014 });
              vfx.burst(q, { n: 1, color: '#6d6458', speed: 0.02, up: 0.03, size: 0.45, life: 12, smoke: true });
              vfx.ring(q, 0.05, 0.5, '#dfe7ef', 5, 0.5);
            });
          }
          this.c.shake(0.12);
        } else if (fresh && s.tick % 3 === 0) vfx.burst(this.v.copy(p).lerp(b, Math.random()).clone().setY(p.y + 0.1), { n: 1, color: '#dfe7ef', speed: 0.02, up: 0.04, size: 0.07, life: 6, gravity: 0.008 });
        return true;
      }
      case 'fireLine': {
        // Estela: a line of fire laid along the leap, from the burst to its target.
        const [col, dk, lt] = TYPE_COLOR[z.dt ?? 'fire'] ?? (TYPE_COLOR.fire as [THREE.Color, string, string]);
        const b = this.c.at(z.tx ?? z.x, z.ty ?? z.y, 0.08);
        const flick = 0.75 + Math.random() * 0.25;
        this.line(p, b, col, rr * 1.8, 0.5 * flick, 0.4);
        this.line(p, b, z.dt ? C(lt) : EMBER_LT, rr * 0.7, 0.55 * flick, 0.55);
        if (fresh) for (let i = 0; i < this.n(2, 1); i++) vfx.burst(this.v.copy(p).lerp(b, Math.random()).clone().setY(p.y + 0.08), { n: 1, color: i ? lt : dk, speed: 0.015, up: 0.06, size: 0.16, life: 9, gravity: -0.003 });
        return true;
      }
      case 'toll': {
        // Tañido: the smitten rings like a bell; a cold halo on it, the slowed around under a pale haze.
        this.c.glow.add(p.x, p.y + 1.6, p.z, HOLY, 0.7, 0.55);
        this.rim(p, rr, HOLY_BLUE, 10, 0.32, 0.22, t, 0.25);
        return true;
      }
      case 'oldRoot': {
        // El bosque viejo: a knot of old root lying in wait where the held one died, green sap pulsing in it.
        const k = 0.5 + 0.5 * Math.sin(t * 3 + z.id);
        this.c.glow.add(p.x, p.y + 0.05, p.z, ROOT, rr * 2.4, 0.4);
        this.rim(p, rr * 0.8, LEAF_LT, 5, 0.18, 0.25 + 0.2 * k, t, 0.02);
        if (fresh && (s.tick + z.id) % 18 === 0) vfx.burst(p.clone().setY(p.y + 0.1), { n: 2, color: '#6fa05a', speed: 0.02, up: 0.04, size: 0.1, life: 12, gravity: 0.004 });
        return true;
      }
      case 'parapet': {
        // Parapeto: a wall of steel light rising around the warrior, its allies behind it.
        this.rim(p, rr, STEEL, 14, 0.42, 0.28, t, 0.05);
        this.rim(p, rr * 0.98, DRAGON, 4, 0.3, 0.3, t, -0.08);
        if (fresh && s.tick % 10 === 0) vfx.ring(p, rr * 0.8, rr, '#c4c8d2', 8, 0.4);
        return true;
      }
      case 'puddle': {
        // Charco: Maelis's remedy spilt on the ground, green-teal and bubbling.
        this.c.glow.add(p.x, p.y + 0.04, p.z, REMEDY, rr * 1.7, 0.16);
        if (fresh && s.tick % 2 === 0) vfx.burst(this.inDisc(p, rr * 0.85), { n: 1, color: '#bff5e0', speed: 0.004, up: 0.035, size: 0.1, life: 10, gravity: -0.002 });
        if (fresh && z.every > 0 && z.ticks % z.every === 0) vfx.ring(p, rr * 0.1, rr, '#7fe0c0', 8, 0.5);
        return true;
      }
      case 'vigil': {
        // Velada: a circle of cold light where no nightmare can stand; pale pillars of light on its edge.
        this.c.glow.add(p.x, p.y + 0.05, p.z, HOLY, rr * 1.6, 0.1);
        this.rim(p, rr, PURE, 16, 0.4, 0.45, t, 0.03);
        if (fresh && s.tick % 3 === 0) {
          const a = Math.random() * TAU;
          const q = this.w.set(p.x + Math.cos(a) * rr, p.y, p.z + Math.sin(a) * rr);
          vfx.shaft(q, this.pq.copy(q).setY(q.y + 1.4), '#eef2ff', 6, 0.12);
        }
        return true;
      }
      case 'sprout': {
        // El jardín: a green shoot of the bloom, healing by itself, leaves rising off it.
        const k = 0.5 + 0.5 * Math.sin(t * 2 + z.id);
        this.c.glow.add(p.x, p.y + 0.05, p.z, LEAF, rr * 1.4, 0.14);
        this.c.glow.add(p.x, p.y + 0.35, p.z, LEAF_LT, 0.45, 0.5 + 0.3 * k);
        if (fresh && (s.tick + z.id) % 3 === 0) vfx.burst(this.inDisc(p, rr * 0.4, 0.2), { n: 1, color: '#b3f59a', speed: 0.004, up: 0.03, size: 0.1, life: 16, gravity: -0.002 });
        if (fresh && z.every > 0 && z.ticks % z.every === 0) vfx.ring(p, 0.1, rr, '#b3f59a', 8, 0.45);
        return true;
      }
    }
    return false;
  }

  /** The marks a form leaves on its skill's own zone (`fm`): drawn over the skill's look. */
  mark(s: BattleState, z: Zone, p: THREE.Vector3, fresh: boolean, t: number): void {
    if (!FORM_FX.on) return;
    const fm = z.fm ?? 0;
    const rr = z.radius * this.c.k;
    const { vfx } = this.c;
    if (z.kind === 'storm' && fm & 1) {
      // Ojo quieto: the eye of the storm gathers while the mage stands; charged, it blazes white and cracks.
      const k = Math.min(1, (z.still ?? 0) / 24);
      // Its eye rides high over the mage, a ring more than a fill, so the mage and its name stay clear under it.
      this.c.glow.add(p.x, p.y + 3.6, p.z, BOLT, 0.45 + 0.45 * k, 0.16 + 0.26 * k);
      this.rim(this.pq.set(p.x, p.y + 3.5, p.z), 0.35 + 0.3 * k, k >= 1 ? BOLT_LT : BOLT, 8, 0.14, 0.25 + 0.35 * k, t, 0.5);
      if (k >= 1) {
        this.rim(p, rr * 0.35, BOLT_LT, 8, 0.3, 0.4, t, 0.4);
        if (fresh && s.tick % 6 === 0) vfx.ring(p, rr * 0.05, rr * 0.4, '#dff0ff', 5, 0.6);
      }
    } else if (z.kind === 'rain' && fm & 2) {
      // Lluvia que sigue: a red mark on the one it hunts.
      this.rim(p, 0.5, DRAGON, 6, 0.22, 0.45, t, 0.5);
    } else if (z.kind === 'sanctuary') {
      // Paso firme: six standing stones of light on its edge; Atrio: a pale frost at its edge (what comes in slows).
      if (fm & 1) this.rim(p, rr * 0.95, STEEL, 6, 0.38, 0.45, t, 0);
      if (fm & 2) {
        this.rim(p, rr, HOLY_BLUE, 18, 0.28, 0.25, t, -0.04);
        if (fresh && s.tick % 12 === 6) vfx.ring(p, rr, rr * 0.7, '#cfe0ff', 8, 0.35);
      }
    } else if (z.kind === 'hallow' && z.hop) {
      // Llama que salta: the leapt ground burns brighter on the ally it found.
      this.rim(p, rr * 0.9, PURE, 8, 0.35, 0.4, t, 0.2);
    } else if (z.kind === 'bloom') {
      // Maraña: vines curling at its edge; Polen: a pale chill of pollen drifting over it.
      if (fm & 1) {
        this.rim(p, rr * 0.92, ROOT, 10, 0.32, 0.35, t, 0.03);
        if (fresh && s.tick % 8 === 0) vfx.burst(this.inDisc(p, rr * 0.9), { n: 2, color: '#4f7a3a', speed: 0.02, up: 0.05, size: 0.09, life: 9, gravity: 0.006 });
      }
      if (fm & 2 && fresh && s.tick % 2 === 0) vfx.burst(this.inDisc(p, rr * 0.9, 0.4), { n: 1, color: '#e4f5d8', speed: 0.01, up: 0.008, size: 0.16, life: 22, gravity: -0.0005, spread: 0.2 });
      if (fm & 2) this.c.glow.add(p.x, p.y + 0.5, p.z, POLLEN, rr * 1.2, 0.08);
    }
  }

  /* ———————————————— what bursts ———————————————— */

  /** A form's burst; false when the kind isn't one of them. */
  boom(kind: string, p: THREE.Vector3, r: number): boolean {
    const { vfx } = this.c;
    const flare = this.fl;
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    const low = this.c.low();
    switch (kind) {
      case 'heavySmite':
        // Martillo pesado: the hammer comes down heavy: a crater of cold light, the ground shaken.
        vfx.shaft(up(3.2), p, '#eef2ff', 5, 0.55);
        vfx.ring(p, 0.1, r * 1.2, '#eef2ff', 8, 0.85);
        vfx.ring(p, 0.1, r * 0.7, '#cfe0ff', 11, 0.5);
        vfx.burst(up(0.15), { n: low ? 6 : 14, color: '#cfc6b8', speed: 0.07, up: 0.09, size: 0.1, life: 10, gravity: 0.014 });
        flare(up(0.4), '#eef2ff', 2.4, 7);
        this.c.shake(0.16);
        return true;
      case 'secondDawn':
        // Segundo amanecer: a little sun is lit over the ally, to wait.
        flare(up(3.2), '#eef2ff', 1.6, 8);
        vfx.ring(up(3.2), 0.05, 0.6, '#cfe0ff', 8, 0.7);
        vfx.burst(up(3.2), { n: low ? 4 : 8, color: '#ffffff', speed: 0.03, up: 0.01, size: 0.07, life: 12, gravity: -0.001 });
        return true;
      case 'crescendo': {
        // Crescendo: every leap of the chain lands bigger: a wave that grows with it.
        const k = Math.max(1, r / (900 * this.c.k));
        vfx.ring(p, 0.2, r * 1.3, '#ff8a6e', 7 + Math.round(k * 2), Math.min(1, 0.4 + 0.2 * k));
        vfx.ring(up(0.25), 0.2, r * 1.05, '#dfe7ef', 6, 0.5);
        if (k > 1.2) vfx.ring(p, r * 0.8, r * 1.7, '#ff4f3a', 9, 0.35);
        vfx.burst(up(0.15), { n: Math.round((low ? 4 : 8) * k), color: '#8a8172', speed: 0.06 * k, up: 0.05, size: 0.3, life: 10, smoke: true });
        this.c.shake(Math.min(0.3, 0.08 * k));
        return true;
      }
      case 'hunger':
        // Hambre: the chained leap's heal, seen double: two red-green rings rising one after the other.
        vfx.ring(up(0.3), 0.9, 0.2, '#ff8a6e', 7, 0.7);
        vfx.ring(up(0.3), 0.6, 0.15, '#b3f59a', 7, 0.7);
        this.c.later(3, () => {
          vfx.ring(up(0.9), 0.9, 0.2, '#ff8a6e', 7, 0.6);
          vfx.ring(up(0.9), 0.6, 0.15, '#b3f59a', 7, 0.6);
        });
        vfx.burst(up(1), { n: low ? 5 : 10, color: '#ff6a5a', speed: 0.02, up: 0.05, size: 0.08, life: 14, gravity: -0.003 });
        flare(up(1), '#b3f59a', 1.4, 6);
        return true;
      case 'endless':
        // Salto sin fin: the leap is ready again: a red trail coils up where it landed, ready to jump.
        for (let i = 0; i < (low ? 4 : 8); i++) {
          const a = (i / (low ? 4 : 8)) * TAU;
          const q = this.v.set(p.x + Math.cos(a) * 0.6, p.y + 0.1 + i * 0.12, p.z + Math.sin(a) * 0.6).clone();
          this.c.later(i, () => vfx.burst(q, { n: 2, color: '#ff4f3a', speed: 0.02, up: 0.04, size: 0.1, life: 10 }));
        }
        vfx.shaft(p, up(2.2), '#ff8a6e', 6, 0.25);
        vfx.ring(p, 0.2, r, '#ff4f3a', 7, 0.6);
        return true;
      case 'bashWave':
        // Onda: the bash throws a wave of air and dust out of its target.
        vfx.ring(p, r * 0.2, r * 1.15, '#dfe7ef', 7, 0.8);
        vfx.ring(up(0.3), r * 0.3, r * 1.3, '#ff8a6e', 6, 0.35);
        for (let i = 0; i < (low ? 6 : 12); i++) {
          const a = (i / (low ? 6 : 12)) * TAU;
          vfx.burst(this.v.set(p.x + Math.cos(a) * r * 0.7, p.y + 0.15, p.z + Math.sin(a) * r * 0.7), { n: 1, color: '#7d766c', speed: 0.05, up: 0.02, size: 0.38, life: 10, smoke: true });
        }
        this.c.shake(0.14);
        return true;
      case 'riftStun':
        // One caught crossing the cleft: grit bursts up under it.
        vfx.burst(up(0.15), { n: low ? 3 : 6, color: '#cfc6b8', speed: 0.05, up: 0.08, size: 0.08, life: 8, gravity: 0.012 });
        vfx.ring(p, 0.05, 0.45, '#ff8a6e', 5, 0.55);
        return true;
      case 'gateShut':
        // Portón cerrado: the boss is struck still, as by a gate slamming: a heavy white jolt and iron sparks.
        flare(up(1.2), '#ffffff', 2.4, 4);
        vfx.ring(p, 0.2, r * 1.5, '#c4c8d2', 8, 0.9);
        vfx.ring(up(0.4), 0.2, r, '#ff8a6e', 6, 0.6);
        vfx.burst(up(1), { n: low ? 8 : 16, color: '#ffe8d8', speed: 0.12, up: 0.06, size: 0.07, life: 6 });
        this.c.shake(0.3);
        return true;
      case 'anchor':
        // Ancla: the barbarian sets its feet; a ring of earth heaves in toward it from far out.
        vfx.ring(p, r, r * 0.15, '#8a8172', 9, 0.7);
        vfx.ring(p, r * 0.7, r * 0.1, '#c4c8d2', 7, 0.45);
        vfx.burst(up(0.1), { n: low ? 6 : 12, color: '#6d6458', speed: 0.06, up: 0.03, size: 0.4, life: 12, smoke: true, spread: 0.6 });
        this.c.shake(0.12);
        return true;
      case 'bloodSwirl':
        // Remolino de sangre: what it pulled bleeds into the barbarian: red motes drawn in.
        vfx.implode(up(0.8), r, { n: low ? 8 : 18, color: '#ff4f3a', size: 0.08, life: 12, swirl: 0.6, lift: 0.3 });
        flare(up(0.9), '#ff6a4a', 1.2, 5);
        return true;
      case 'toll':
        // Tañido: the bell-ring from the smitten: two cold rings rolling out.
        vfx.ring(up(0.2), 0.2, r, '#eef2ff', 7, 0.6);
        vfx.ring(up(1.2), 0.1, r * 0.5, '#cfe0ff', 5, 0.45);
        return true;
      case 'gasparGuard':
        // La guardia de Gaspar: the old garrison's spectre rises in a column of cold light.
        vfx.shaft(p, up(3), '#eef2ff', 8, 0.5);
        vfx.ring(p, 0.2, r, '#ffffff', 9, 0.8);
        vfx.burst(up(0.4), { n: low ? 8 : 18, color: '#dfe8ff', speed: 0.05, up: 0.09, size: 0.1, life: 14, gravity: -0.003 });
        flare(up(1.3), '#eef2ff', 2.6, 8);
        return true;
      case 'oldRoot':
        vfx.burst(up(0.1), { n: low ? 4 : 8, color: '#4f7a3a', speed: 0.05, up: 0.07, size: 0.1, life: 10, gravity: 0.01 });
        vfx.ring(p, 0.05, r * 1.4, '#7fc06a', 6, 0.55);
        return true;
      case 'rootSnap':
        // The root closes on the one that stepped on it: roots whip up around it.
        for (let i = 0; i < (low ? 3 : 6); i++) {
          const a = (i / (low ? 3 : 6)) * TAU;
          const q = this.v.set(p.x + Math.cos(a) * 0.35, p.y, p.z + Math.sin(a) * 0.35).clone();
          vfx.beam(q, q.clone().setY(q.y + 0.9).lerp(p, 0.5), '#4f7a3a', 6, 0.06, 0.2);
        }
        vfx.burst(up(0.2), { n: low ? 4 : 10, color: '#7fc06a', speed: 0.06, up: 0.06, size: 0.08, life: 10, gravity: 0.01 });
        return true;
      case 'lastTurn':
        // El último turno: the warrior refuses to fall: a red-white blaze and a ring of steel.
        flare(up(1), '#ffffff', 2.2, 5);
        flare(up(1), '#ff4f3a', 3.2, 10);
        vfx.ring(p, 0.2, r, '#ff8a6e', 10, 0.9);
        vfx.shaft(p, up(2.6), '#ff8a6e', 8, 0.35);
        this.c.shake(0.2);
        return true;
      case 'unfallen':
        // No cae: the frenzy holds it up: a burst of red fury.
        flare(up(1), '#ff4f3a', 2.4, 6);
        vfx.burst(up(0.9), { n: low ? 8 : 16, color: '#ff6a3a', speed: 0.1, up: 0.06, size: 0.1, life: 8 });
        vfx.ring(p, 0.1, r, '#ff6a4a', 6, 0.7);
        return true;
      case 'counterFrost':
        // Contraescarcha: the one that struck the mage is rimed.
        vfx.burst(up(0.8), { n: low ? 4 : 8, color: '#e8f7ff', speed: 0.04, up: 0.02, size: 0.09, life: 10, gravity: 0.003 });
        flare(up(0.8), '#9fdcff', 1, 4);
        return true;
      case 'hug':
        // Abrazo: the bear's grip: a squeeze of bark and moss.
        vfx.ring(up(0.3), 0.6, 0.15, '#7a5a3a', 6, 0.7);
        vfx.burst(up(0.6), { n: low ? 3 : 6, color: '#6b4a2a', speed: 0.04, up: 0.03, size: 0.09, life: 8, gravity: 0.01 });
        return true;
      case 'forgeRebuild':
        // Fragua: the automaton is forged again from its own wreck: sparks fly up and the iron glows.
        flare(up(0.8), '#ffb070', 2.6, 10);
        vfx.burst(up(0.4), { n: low ? 12 : 26, color: '#ffb347', speed: 0.1, up: 0.12, size: 0.07, life: 12, gravity: 0.012 });
        vfx.shaft(p, up(2.2), '#ff8a2a', 7, 0.3);
        vfx.ring(p, 0.2, r, '#ff6a2a', 8, 0.8);
        this.c.shake(0.12);
        return true;
      case 'lightWard':
        // Escudo de luz: what the lamp had left over closes round the ally as a shell of light.
        vfx.ring(up(0.4), 0.7, 0.3, '#eef2ff', 7, 0.6);
        flare(up(1), '#eef2ff', 1.2, 5);
        return true;
      case 'lance':
        // Lanza de la atalaya: the last one pinned: a white nail of light through it into the ground.
        vfx.shaft(up(1.8), p, '#ffffff', 5, 0.12);
        vfx.ring(p, 0.05, 0.6, '#ff8a6e', 6, 0.7);
        vfx.burst(up(0.2), { n: low ? 3 : 6, color: '#8a8172', speed: 0.05, up: 0.06, size: 0.08, life: 8, gravity: 0.012 });
        return true;
      case 'lostTrail':
        // Huella perdida: the ranger melts into a wisp of grey.
        vfx.burst(up(0.8), { n: low ? 4 : 10, color: '#5a6066', speed: 0.03, up: 0.02, size: 0.5, life: 16, smoke: true, spread: 0.3 });
        return true;
      case 'coldBlood':
        // Sangre fría: a pale blue calm over the frenzy's red.
        vfx.ring(up(0.2), 0.2, r, '#cfe0ff', 8, 0.6);
        flare(up(1), '#cfe0ff', 1.6, 6);
        return true;
      case 'boarCharge':
        // Jabalí: the charge lands: a crash of earth and a stunned beast.
        vfx.ring(p, 0.2, r * 1.2, '#8a8172', 8, 0.8);
        vfx.burst(up(0.3), { n: low ? 8 : 16, color: '#6d6458', speed: 0.1, up: 0.07, size: 0.42, life: 12, smoke: true, spread: 0.4 });
        flare(up(0.8), '#ff8a6e', 1.6, 4);
        this.c.shake(0.2);
        return true;
      case 'steelEcho':
        // Eco de acero: the aegis breaks with a ring of steel that stuns those it calls.
        vfx.ring(up(0.3), 0.3, r, '#c4c8d2', 8, 0.9);
        vfx.ring(up(0.6), 0.2, r * 0.75, '#ffffff', 6, 0.6);
        vfx.burst(up(0.8), { n: low ? 8 : 18, color: '#e8ecf2', speed: 0.14, up: 0.04, size: 0.07, life: 6 });
        this.c.shake(0.18);
        return true;
      case 'steelOath':
        // Juramento de acero: the shield rises again round the paladin, plate by plate.
        vfx.ring(up(0.1), r * 1.2, 0.2, '#eef2ff', 9, 0.8);
        vfx.shaft(p, up(2.4), '#eef2ff', 7, 0.3);
        flare(up(1.1), '#ffffff', 2, 6);
        return true;
      case 'oldBear':
        // El oso viejo: the bark the bear shed stands up as a bear of its own.
        vfx.burst(up(0.4), { n: low ? 8 : 16, color: '#6b4a2a', speed: 0.06, up: 0.08, size: 0.12, life: 14, gravity: 0.01 });
        vfx.burst(up(0.8), { n: low ? 4 : 8, color: '#7fc06a', speed: 0.04, up: 0.05, size: 0.1, life: 14, gravity: -0.002 });
        vfx.ring(p, 0.2, r, '#7fc06a', 8, 0.7);
        return true;
      case 'medal':
        // La medalla: the oath goes off by itself: the medal flashes red and the oath's light follows.
        flare(up(1.6), '#ff4f3a', 2.6, 8);
        vfx.ring(p, 0.3, r, '#ff8a6e', 10, 0.8);
        vfx.shaft(p, up(3), '#ffd8c8', 8, 0.25);
        return true;
      case 'guardingDawn':
        // Alba que guarda: the raised wake inside a shell of dawn.
        vfx.ring(up(0.5), 0.8, 0.35, '#ffffff', 8, 0.7);
        vfx.burst(up(1), { n: low ? 4 : 10, color: '#eef2ff', speed: 0.04, up: 0.05, size: 0.08, life: 12, gravity: -0.002 });
        return true;
      case 'closedSky':
        // Cielo cerrado: the sky closes again over the dead: a dark ring and a flight of arrows overhead.
        vfx.ring(p, r * 1.2, r * 0.4, '#c4ccd8', 9, 0.6);
        vfx.burst(up(4), { n: low ? 8 : 16, color: '#dfe8f4', speed: 0.06, up: -0.12, size: 0.05, life: 10, gravity: 0.01, spread: r * 0.5 });
        return true;
      case 'garden':
        // El jardín: the bloom seeds the ground: three shoots break through.
        vfx.ring(p, 0.3, r, '#b3f59a', 10, 0.7);
        vfx.burst(up(0.2), { n: low ? 8 : 18, color: '#7fc06a', speed: 0.06, up: 0.08, size: 0.1, life: 14, gravity: 0.004 });
        return true;
      case 'doubleNet':
        vfx.ring(up(0.2), 0.1, r, '#c4c8d2', 6, 0.7);
        return true;
      case 'flasks':
        // Lluvia de frascos: the grenade splits mid-air in three, a crack of glass and a teal glint.
        flare(up(2), '#7fe0c0', 2.2, 6);
        vfx.burst(up(2), { n: low ? 6 : 14, color: '#bff5e0', speed: 0.12, up: 0.02, size: 0.06, life: 8, gravity: 0.008 });
        return true;
      case 'spikes':
        // Púas: the one stepping in is nailed by the rain.
        vfx.ring(p, 0.05, 0.4, '#dfe8f4', 5, 0.6);
        vfx.burst(up(0.15), { n: low ? 2 : 4, color: '#8a8172', speed: 0.04, up: 0.05, size: 0.07, life: 7, gravity: 0.012 });
        return true;
      case 'tangle':
        // Maraña: vines catch the one that walked in.
        vfx.ring(p, 0.5, 0.1, '#4f7a3a', 6, 0.6);
        vfx.burst(up(0.2), { n: low ? 2 : 5, color: '#6fa05a', speed: 0.04, up: 0.06, size: 0.08, life: 9, gravity: 0.01 });
        return true;
      case 'vengeance':
        // Venganza: all that the challenge struck back, at once: a red blast round the warrior.
        flare(up(1), '#ffffff', 2, 4);
        flare(up(0.8), '#ff4f3a', 3.4, 8);
        vfx.ring(p, 0.3, r, '#ff4f3a', 9, 1);
        vfx.ring(up(0.3), 0.2, r * 0.7, '#ffd8c8', 6, 0.7);
        vfx.burst(up(0.6), { n: low ? 12 : 26, color: '#ff8a6e', speed: 0.15, up: 0.06, size: 0.1, life: 8 });
        this.c.shake(0.28);
        return true;
      case 'bitterSap':
        return true;
    }
    return false;
  }

  /** A form's line; false when the kind isn't one of them. */
  beam(kind: string, a: THREE.Vector3, b: THREE.Vector3): boolean {
    const { vfx } = this.c;
    const flare = this.fl;
    const low = this.c.low();
    switch (kind) {
      case 'highLamp':
        // Lámpara alta: a second ray of the lamp's light, from the most hurt to the next one.
        vfx.shaft(a.clone().setY(a.y + 1), b.clone().setY(b.y + 1), '#eef2ff', 6, 0.16);
        vfx.beam(a.clone().setY(a.y + 1), b.clone().setY(b.y + 1), '#ffffff', 4, 0.05, 0);
        vfx.ring(b, 0.6, 0.15, '#cfe0ff', 7, 0.6);
        this.fl(b.clone().setY(b.y + 1), '#eef2ff', 1.2, 6);
        return true;
      case 'discharge':
        // Descarga: the beam between the turrets spits a fork of lightning.
        vfx.beam(a, b, '#8fb4ff', 4, 0.07, 0.8);
        vfx.beam(a, b, '#ffffff', 3, 0.025, 0.6);
        vfx.burst(b, { n: low ? 2 : 5, color: '#dff0ff', speed: 0.07, up: 0.04, size: 0.06, life: 4 });
        flare(b, '#bcd2ff', 1.2, 3);
        return true;
      case 'lucerna':
        // Lucerna: a broad beam of cold light down the line, healing as it passes.
        vfx.shaft(a, b, '#eef2ff', 7, 0.5);
        vfx.beam(a, b, '#ffffff', 4, 0.08, 0);
        vfx.beam(a, b, '#cfe0ff', 6, 0.18, 0);
        for (let i = 1; i <= (low ? 3 : 6); i++) vfx.burst(a.clone().lerp(b, i / (low ? 4 : 7)), { n: 1, color: '#ffffff', speed: 0.02, up: 0.04, size: 0.1, life: 12, gravity: -0.002 });
        flare(a, '#ffffff', 1.6, 4);
        return true;
      case 'relay':
        // Relevo: the broken aegis hands a shield on: a thread of white and steel to the hurt ally.
        vfx.beam(a, b, '#eef2ff', 7, 0.05, 0.2);
        vfx.beam(a, b, '#c4c8d2', 6, 0.02, 0.3);
        vfx.ring(b.clone().setY(b.y - 0.5), 0.8, 0.3, '#eef2ff', 7, 0.6);
        return true;
      case 'stormJump':
        // Rayo que salta: a bolt leaps off the storm's edge to one more.
        vfx.beam(a.clone().setY(a.y + 2), b, '#ffffff', 2, 0.035, 0.8);
        vfx.beam(a.clone().setY(a.y + 2), b, '#8fb4ff', 3, 0.015, 1.1);
        vfx.ring(b.clone().setY(b.y - 0.5), 0.1, 0.6, '#cfe0ff', 4, 0.8);
        return true;
      case 'leapFlame':
        // Llama que salta: the hallowed ground arcs across to the beset ally.
        vfx.beam(a, b, '#ffffff', 6, 0.08, 0.15);
        vfx.shaft(b.clone().setY(b.y - 0.5), b.clone().setY(b.y + 2), '#eef2ff', 8, 0.3);
        flare(b, '#eef2ff', 2, 6);
        return true;
      case 'boarCharge':
        // Jabalí: a streak of dust where the barbarian ran.
        vfx.shaft(a.clone().setY(a.y - 0.4), b.clone().setY(b.y - 0.4), '#8a8172', 6, 0.35);
        vfx.beam(a, b, '#ff8a6e', 3, 0.03, 0);
        return true;
    }
    return false;
  }
}
